import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAdminAuth } from "../context/AdminAuthContext";
import {
  ROLE_LABELS,
  canGrantRole,
  canManageAdmins,
  getAssignableRoles,
} from "../utils/adminRoles";

const Users = () => {
  const { user: currentUser, role: currentRole } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleSelection, setRoleSelection] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const trimmedSearch = search.trim();
  const isPrefixSearch = trimmedSearch.endsWith("*");
  const queryText = isPrefixSearch
    ? trimmedSearch.slice(0, -1).trim()
    : trimmedSearch;
  const hasSearch = queryText.length > 0;

  useEffect(() => {
    let isActive = true;

    const loadUsers = async () => {
      if (!hasSearch) {
        setUsers([]);
        setSelectedUser(null);
        setRoleSelection("");
        setLoading(false);
        return;
      }

      setUsers([]);
      setSelectedUser(null);
      setRoleSelection("");
      setLoading(true);
      try {
        const userCollection = collection(db, "users");
        const userQuery = isPrefixSearch
          ? query(
              userCollection,
              where("username", ">=", queryText),
              where("username", "<=", `${queryText}\uf8ff`),
              orderBy("username"),
              limit(200)
            )
          : query(
              userCollection,
              where("username", "==", queryText),
              limit(200)
            );
        const snap = await getDocs(userQuery);
        if (!isActive) return;
        const list = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setUsers(list);
      } catch (error) {
        console.error("Failed to load users", error);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      isActive = false;
    };
  }, [hasSearch, isPrefixSearch, queryText]);

  const availableRoles = getAssignableRoles(currentRole);

  const handleSelectUser = (item) => {
    setSelectedUser(item);
    const nextRole = availableRoles.includes(item.adminRole)
      ? item.adminRole
      : "";
    setRoleSelection(nextRole);
  };
  const canEditAdmins = canManageAdmins(currentRole);
  const canRemoveRole =
    canEditAdmins &&
    (currentRole === "developer" ||
      (currentRole === "super_admin" &&
        selectedUser?.adminRole !== "developer" &&
        selectedUser?.adminRole !== "super_admin"));

  const updateRole = async () => {
    if (!selectedUser || !roleSelection || !currentUser) return;
    if (!canGrantRole(currentRole, roleSelection)) return;

    setSaving(true);
    try {
      const userRef = doc(db, "users", selectedUser.id);
      await updateDoc(userRef, {
        adminRole: roleSelection,
        adminEnabled: true,
        adminUpdatedAt: serverTimestamp(),
        adminUpdatedBy: currentUser.uid,
        adminUpdatedByRole: currentRole,
      });

      setUsers((prev) =>
        prev.map((userItem) =>
          userItem.id === selectedUser.id
            ? {
                ...userItem,
                adminRole: roleSelection,
                adminEnabled: true,
              }
            : userItem
        )
      );
      setSelectedUser((prev) =>
        prev
          ? {
              ...prev,
              adminRole: roleSelection,
              adminEnabled: true,
            }
          : prev
      );
    } catch (error) {
      console.error("Failed to update admin role", error);
    } finally {
      setSaving(false);
    }
  };

  const removeRole = async () => {
    if (!selectedUser || !currentUser) return;
    if (!canRemoveRole) return;

    setSaving(true);
    try {
      const userRef = doc(db, "users", selectedUser.id);
      await updateDoc(userRef, {
        adminRole: null,
        adminEnabled: false,
        adminUpdatedAt: serverTimestamp(),
        adminUpdatedBy: currentUser.uid,
        adminUpdatedByRole: currentRole,
      });

      setUsers((prev) =>
        prev.map((userItem) =>
          userItem.id === selectedUser.id
            ? {
                ...userItem,
                adminRole: null,
                adminEnabled: false,
              }
            : userItem
        )
      );
      setSelectedUser((prev) =>
        prev
          ? {
              ...prev,
              adminRole: null,
              adminEnabled: false,
            }
          : prev
      );
      setRoleSelection("");
    } catch (error) {
      console.error("Failed to remove admin role", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <p>Search by username to review status and assign admin roles.</p>
        </div>
        <div className="page-actions">
          <input
            className="search-input"
            type="search"
            placeholder="Search by username (add * for prefix)"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      <div className="grid-2">
        <div className="card table-card">
          <div className="card-header">
            <h3>Search results</h3>
            {hasSearch ? (
              <span className="chip">{users.length} results</span>
            ) : null}
          </div>
          {!hasSearch ? (
            <div className="empty-inline">Enter a username to search.</div>
          ) : loading ? (
            <div className="empty-inline">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="empty-inline">No users found.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Contact</th>
                    <th>KYC</th>
                    <th>Admin Role</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((item) => (
                    <tr
                      key={item.id}
                      className={
                        selectedUser?.id === item.id ? "active-row" : ""
                      }
                      onClick={() => handleSelectUser(item)}
                    >
                      <td>
                        <div className="table-title">
                          {item.username || "Unknown"}
                        </div>
                        <div className="table-subtitle">{item.id}</div>
                      </td>
                      <td>
                        <div className="table-title">
                          {item.email || "No email"}
                        </div>
                        <div className="table-subtitle">
                          {item.phoneNumber || "No phone"}
                        </div>
                      </td>
                      <td>
                        {(() => {
                          const status =
                            item.kycStatus || item.kyc?.status || "unverified";
                          const normalized = String(status).toLowerCase();
                          const statusClass =
                            normalized === "approved" ||
                            normalized === "verified"
                              ? "success"
                              : normalized === "rejected"
                              ? "danger"
                              : normalized === "submitted" ||
                                normalized === "pending"
                              ? "warning"
                              : "muted";
                          return (
                            <span className={`status-pill ${statusClass}`}>
                              {status}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {item.adminRole ? (
                          <span className="status-pill accent">
                            {ROLE_LABELS[item.adminRole] || item.adminRole}
                          </span>
                        ) : (
                          <span className="status-pill muted">None</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card detail-card">
          <div className="card-header">
            <h3>User details</h3>
          </div>
          {!hasSearch ? (
            <div className="empty-inline">
              Search for a username to view details.
            </div>
          ) : !selectedUser ? (
            <div className="empty-inline">Select a user to inspect.</div>
          ) : (
            <div className="detail-body">
              <div className="detail-row">
                <div className="detail-label">UID</div>
                <div className="detail-value">{selectedUser.id}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Username</div>
                <div className="detail-value">
                  {selectedUser.username || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Email</div>
                <div className="detail-value">
                  {selectedUser.email || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Phone</div>
                <div className="detail-value">
                  {selectedUser.phoneNumber || "N/A"}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">KYC</div>
                <div className="detail-value">
                  {selectedUser.kycStatus || selectedUser.kyc?.status || "N/A"}
                </div>
              </div>

              <div className="divider" />

              <div className="detail-row">
                <div className="detail-label">Admin role</div>
                <div className="detail-value">
                  {selectedUser.adminRole
                    ? ROLE_LABELS[selectedUser.adminRole] ||
                      selectedUser.adminRole
                    : "None"}
                </div>
              </div>

              {canEditAdmins ? (
                <div className="form-stack">
                  <label className="field">
                    <span>Assign role</span>
                    <select
                      value={roleSelection}
                      onChange={(event) =>
                        setRoleSelection(event.target.value)
                      }
                    >
                      <option value="">Select role</option>
                      {availableRoles.map((roleItem) => (
                        <option key={roleItem} value={roleItem}>
                          {ROLE_LABELS[roleItem] || roleItem}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="inline-actions">
                    <button
                      className="button primary"
                      type="button"
                      onClick={updateRole}
                      disabled={
                        saving ||
                        !roleSelection ||
                        !canGrantRole(currentRole, roleSelection)
                      }
                    >
                      {saving ? "Saving..." : "Update role"}
                    </button>
                    <button
                      className="button danger"
                      type="button"
                      onClick={removeRole}
                      disabled={saving || !selectedUser.adminRole || !canRemoveRole}
                    >
                      Remove role
                    </button>
                  </div>
                </div>
              ) : (
                <div className="empty-inline">
                  You do not have permission to manage admin roles.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
